import { useCallback } from 'react'
import {
  type DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import {
  type ColumnOrderState,
  type OnChangeFn,
} from '@tanstack/react-table'

// Drag and keyboard column reordering, shared so every table keeps the same activation
// thresholds. Those thresholds are the point: a 6px mouse distance and a 150ms touch delay
// stop a drag starting on what was meant as a click — the same header toggles sorting.
//
// Needs the table's visible column ids for keyboard moves, so call it after `useTable`.
export const useColumnReorder = (
  onColumnOrderChange: OnChangeFn<ColumnOrderState>,
  visibleColumnIds: string[],
) => {
  const handleColumnDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    onColumnOrderChange((previous) => {
      const oldIndex = previous.indexOf(String(active.id))
      const newIndex = previous.indexOf(String(over.id))
      if (oldIndex < 0 || newIndex < 0) return previous

      return arrayMove(previous, oldIndex, newIndex)
    })
  }, [onColumnOrderChange])

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
  )

  const handleKeyboardColumnMove = useCallback((columnId: string, direction: -1 | 1) => {
    const visibleIndex = visibleColumnIds.indexOf(columnId)
    const targetId = visibleColumnIds[visibleIndex + direction]
    if (visibleIndex < 0 || !targetId) return

    onColumnOrderChange((previous) => {
      const oldIndex = previous.indexOf(columnId)
      const newIndex = previous.indexOf(targetId)
      if (oldIndex < 0 || newIndex < 0) return previous

      return arrayMove(previous, oldIndex, newIndex)
    })
  }, [onColumnOrderChange, visibleColumnIds])

  return {
    handleColumnDragEnd,
    handleKeyboardColumnMove,
    sensors,
  }
}
